// FEAT-17 — during a test the candidate cannot right-click or select the
// question content. BLOCKED, NEVER RECORDED: this module has no `emit` and no
// network on purpose. The `right_click_attempt` / `select_attempt` / `copy`
// events were removed on 2026-05-11 after one attempt logged 274 selections
// from simply re-reading a prompt — a signal that is all noise. Preventing the
// action costs the honest candidate nothing and writes nothing.
//
// Default-deny: everything on the page is protected except the candidate's OWN
// answer (a text field or the code editor), so a new answer component is
// covered without anyone remembering to wrap it. The CSS half lives in
// index.css under `html.content-protected`.
//
// A deterrent like the rest of anti-cheat, not a lock: the browser's own menu
// bar and a phone camera still exist.
import { inEditor } from "./index";

export const CONTENT_PROTECTED_CLASS = "content-protected";

const OWN_ANSWER_SELECTOR = 'input, textarea, [contenteditable]:not([contenteditable="false"])';

/** The candidate's own answer: a text field, or the code editor. */
function inOwnAnswer(e: Event): boolean {
  if (inEditor(e)) return true;
  return e.target instanceof Element && e.target.closest(OWN_ANSWER_SELECTOR) !== null;
}

// The browser's context menu is one click from Inspect and Print, so it is
// blocked everywhere — the written-answer box included. Monaco is the one
// exception: it draws its own menu (no Inspect, no Print) from this event.
const onContextMenu = (e: Event): void => {
  if (!inEditor(e)) e.preventDefault();
};

// Selecting, copying, cutting or dragging out: allowed only inside the
// candidate's own answer.
const onGuarded = (e: Event): void => {
  if (!inOwnAnswer(e)) e.preventDefault();
};

const GUARDED = ["selectstart", "copy", "cut", "dragstart"] as const;

let installs = 0;

/** Installs the block; the returned function lifts it. Nested installs count. */
export function installContentProtection(): () => void {
  installs += 1;
  if (installs === 1) {
    document.documentElement.classList.add(CONTENT_PROTECTED_CLASS);
    // Capture phase, like paste: nothing on the page can swallow the event first.
    document.addEventListener("contextmenu", onContextMenu, true);
    for (const type of GUARDED) document.addEventListener(type, onGuarded, true);
  }
  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    installs -= 1;
    if (installs > 0) return;
    document.documentElement.classList.remove(CONTENT_PROTECTED_CLASS);
    document.removeEventListener("contextmenu", onContextMenu, true);
    for (const type of GUARDED) document.removeEventListener(type, onGuarded, true);
    // Lifted mid-page (a relaxed question): nothing was selectable a moment
    // ago, so there is no stale selection to clear.
  };
}
